import { useState } from "react";
import Page from "@/components/layout/Page";
import { ParticleBackground } from "@/components/ParticleBackground";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, Phone } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { ProductModal } from "@/components/ProductModal";
import { products, type Product } from "@/data/products";
import { SEOHead } from "@/components/seo/SEOHead";
import { ProductSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";

// Filter to get only the Access to Work Kit product
const accessToWorkProduct = products.find(p => p.name === "Access to Work Kit");

export default function AccessToWorkKits() {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.slug, // Use full slug as ID to match backend price catalog
      name: product.name,
      slug: product.slug,
      price: product.price,
    });
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <>
      <SEOHead
        title="Access to Work Kits | Workplace Sensory Support Packages"
        description="Complete sensory support kits eligible for Access to Work funding. Perfect for neurodivergent employees seeking workplace adjustments. Includes Samboards, sensory pads, and guidance."
        canonical="/access-to-work-kits"
        keywords="access to work sensory tools, workplace adjustments ADHD, autism workplace support, neurodivergent workplace tools, access to work funding"
      />
      {accessToWorkProduct && (
        <ProductSchema
          name={accessToWorkProduct.name}
          description={accessToWorkProduct.description}
          price={accessToWorkProduct.price}
          image={accessToWorkProduct.image?.src}
          sku="ATW-KIT-001"
        />
      )}
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Access to Work Kits", url: "/access-to-work-kits" }
      ]} />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />

        <Page>
        <section className="pb-24">
          <div className="space-y-4">
            <h1 className="font-heading text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] page-title-gradient">
              Access to Work Kits
            </h1>
            
            <p className="text-lg md:text-xl text-slate-200/85 max-w-3xl leading-relaxed">
              Complete sensory kits designed to support neurodivergent adults in the workplace, eligible for Access to Work funding.
            </p>
          </div>
        </section>

        {/* Access to Work Product - Single Card */}
        {accessToWorkProduct && (
          <section className="pb-16">
            <div className="max-w-5xl mx-auto flex justify-center">
              <article
                className="group relative flex flex-col w-full max-w-sm rounded-3xl border border-white/5 bg-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card"
              >
                {accessToWorkProduct.image && (
                  <div className="w-full h-64 overflow-hidden rounded-t-3xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 flex items-center justify-center">
                    <img 
                      src={accessToWorkProduct.image.src} 
                      alt={accessToWorkProduct.image.alt}
                      className="w-full h-full object-contain p-6"
                    />
                  </div>
                )}
                
                <div className="flex flex-col flex-1 px-6 py-6">
                  <div className="space-y-3 flex-1">
                    {accessToWorkProduct.badge && (
                      <span className="inline-flex items-center rounded-full border border-emerald-300/50 bg-emerald-300/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.15em] text-emerald-200">
                        {accessToWorkProduct.badge}
                      </span>
                    )}
                    <h2 className="text-xl font-semibold text-emerald-50 leading-tight">
                      {accessToWorkProduct.name}
                    </h2>
                    <p className="text-sm leading-relaxed text-slate-200/80">
                      {accessToWorkProduct.description}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col gap-3">
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-emerald-300">
                        £{accessToWorkProduct.price.toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        onClick={() => handleAddToCart(accessToWorkProduct)}
                        className="w-full text-sm rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-medium shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button"
                      >
                        Add to cart
                      </Button>
                      <Button
                        onClick={() => setSelectedProduct(accessToWorkProduct)}
                        variant="outline"
                        className="w-full text-sm rounded-full border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10 hover:text-emerald-200 interactive-glow"
                      >
                        View details
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>
        )}

        <div className="pb-24">
          <div className="space-y-12">
            {/* What Access to Work Is */}
            <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
              <p className="text-base text-slate-300/85 leading-relaxed">
                Access to Work is a UK government scheme that helps pay for practical support if you have a disability or health condition that affects your work. Samboards sensory kits qualify as workplace adjustments under this scheme, providing essential tools for focus, regulation, and wellbeing.
              </p>
            </div>

            {/* What's Included */}
            <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6 flex items-center gap-3">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
                What's Included
              </h2>
              <ul className="space-y-3 text-slate-300/80 leading-relaxed">
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Full-size Samboard for desk or personal workspace</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Sensory Pads for gentle tactile support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Fidget Squares for discreet regulation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Usage guidance booklet tailored for workplace settings</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>All necessary invoices and documentation for your Access to Work claim</span>
                </li>
              </ul>
            </div>

            {/* Who It's For */}
            <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Who It's For</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-200 mb-1">Employees</h3>
                    <p className="text-sm text-slate-300/70">Neurodivergent adults seeking sensory support in the workplace</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-200 mb-1">Employers</h3>
                    <p className="text-sm text-slate-300/70">Organizations creating inclusive, accessible workspaces</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-200 mb-1">Occupational Health</h3>
                    <p className="text-sm text-slate-300/70">OH teams recommending workplace adjustments</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-200 mb-1">Access to Work Assessors</h3>
                    <p className="text-sm text-slate-300/70">Professionals assessing workplace needs and support</p>
                  </div>
                </div>
              </div>
            </div>

            {/* How to Order */}
            <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
              <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-3">
                <FileText className="h-7 w-7 text-emerald-400" />
                How to Order
              </h2>
              <p className="text-base text-slate-300/85 leading-relaxed mb-6">
                We make the Access to Work process simple. Get in touch with us to discuss your needs, and we'll provide everything you need for your claim – including detailed invoices, product specifications, and guidance on submitting your application.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] interactive-button"
                >
                  <Link to="/shop">
                    Shop Access to Work Kits
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10 interactive-glow"
                >
                  <Link to="/contact">Contact for Quote</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Page>

      {selectedProduct && (
        <ProductModal
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          product={selectedProduct}
          onAddToCart={() => handleAddToCart(selectedProduct)}
        />
      )}
      </div>
    </>
  );
}
